/**
 * Progressive Overload Engine — suggests weight/rep progression based on history.
 */

import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';

const log = createLogger('OVERLOAD');

// Upper body exercises (default increment +5 lbs)
const UPPER_BODY_PARTS = ['chest', 'shoulders', 'back', 'arms'];
// Lower body exercises (default increment +5 lbs, but could use +2.5 for accessories)
const LOWER_BODY_PARTS = ['legs'];
// Bodyweight / calisthenics
const BODYWEIGHT_PARTS = ['calisthenics'];

export interface OverloadSuggestion {
  exercise: string;
  lastWeight: number;
  lastReps: number;
  lastSets: number;
  suggestedWeight: number;
  suggestedReps: number;
  reasoning: string;
}

export async function getOverloadSuggestion(
  userId: string,
  exerciseName: string,
): Promise<OverloadSuggestion | null> {
  try {
    // Get exercise info + last lift for this exercise
    const { data: exercises, error: exErr } = await supabase
      .from('exercises')
      .select('id, name, body_part, category')
      .ilike('name', exerciseName)
      .limit(1);

    if (exErr || !exercises || exercises.length === 0) return null;
    const exercise = exercises[0];

    // Get last 3 sessions for this exercise (most recent first)
    const { data: lifts, error: liftErr } = await supabase
      .from('lifts')
      .select('weight, reps, sets, created_at')
      .eq('user_id', userId)
      .eq('exercise_id', exercise.id)
      .order('created_at', { ascending: false })
      .limit(3);

    if (liftErr || !lifts || lifts.length === 0) return null;

    const last = lifts[0];
    const bodyPart = (exercise.body_part ?? '').toLowerCase();
    const isBodyweight = BODYWEIGHT_PARTS.includes(bodyPart) || last.weight === 0;

    // Determine target reps — use last reps as baseline
    const targetReps = last.reps;
    const targetSets = last.sets;

    if (isBodyweight) {
      // Bodyweight: suggest +1 rep per set
      return {
        exercise: exerciseName,
        lastWeight: 0,
        lastReps: last.reps,
        lastSets: last.sets,
        suggestedWeight: 0,
        suggestedReps: last.reps + 1,
        reasoning: `You did ${last.reps} reps last time. Try ${last.reps + 1} reps today.`,
      };
    }

    // Check if they've been progressing or stalling
    const completedAllSets = last.sets >= targetSets;
    const hitTargetReps = last.reps >= targetReps;

    // Check if previous sessions show a failure pattern (decreasing weight)
    const hasFailed = lifts.length >= 2 && lifts[1].weight > last.weight;

    if (hasFailed) {
      // Failed: suggest -10% deload
      const deloadWeight = Math.round(last.weight * 0.9 / 2.5) * 2.5; // round to nearest 2.5
      return {
        exercise: exerciseName,
        lastWeight: last.weight,
        lastReps: last.reps,
        lastSets: last.sets,
        suggestedWeight: deloadWeight,
        suggestedReps: targetReps,
        reasoning: `Deload: try ${deloadWeight}×${targetReps} to rebuild.`,
      };
    }

    if (completedAllSets && hitTargetReps) {
      // Completed all sets at target reps — increase weight
      const increment = UPPER_BODY_PARTS.includes(bodyPart) ? 5 : 5;
      const newWeight = last.weight + increment;
      return {
        exercise: exerciseName,
        lastWeight: last.weight,
        lastReps: last.reps,
        lastSets: last.sets,
        suggestedWeight: newWeight,
        suggestedReps: targetReps,
        reasoning: `You hit ${last.weight}×${last.reps} last time. Try ${newWeight}×${targetReps}.`,
      };
    }

    // Completed but at lower reps — same weight, aim for more reps
    return {
      exercise: exerciseName,
      lastWeight: last.weight,
      lastReps: last.reps,
      lastSets: last.sets,
      suggestedWeight: last.weight,
      suggestedReps: last.reps + 1,
      reasoning: `You did ${last.weight}×${last.reps}. Try ${last.weight}×${last.reps + 1} today.`,
    };
  } catch (error) {
    log.error('getOverloadSuggestion error', error);
    return null;
  }
}
